import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { ApiError, apiJson } from '../../lib/api'
import { useRefetch } from '../../lib/connection'
import { fmtStamp } from '../timesheets/time'
import wages from '../WagesPage.module.css'
import styles from './PushAttempts.module.css'
import { money, plural, rangeLabel } from './format'
import type { PushAttempt, PushStatus } from './types'

// 131 (Kerrin, 2026-10-05). The record of pushes to a provider. A push is
// reserved before it is sent and can end in six states; this says which, and
// never says more than CONTROL knows. "Outcome unknown" means the provider may
// or may not have it: until a person checks the provider and records what they
// found, a merge touching those worker/days and a second push over them are
// refused. Recording it makes no call to the provider.

export const STATUS_LABEL: Record<PushStatus, string> = {
  in_progress: 'Sending / in progress',
  sent: 'Sent',
  failed: 'Failed — nothing sent',
  unknown: 'Outcome unknown — check provider',
  confirmed_sent: 'Confirmed sent',
  confirmed_not_sent: 'Confirmed not sent',
}

const chipOf = (s: PushStatus) => (s === 'unknown' || s === 'in_progress' ? styles.chipWarn : s === 'failed' ? styles.chipBad : s === 'sent' || s === 'confirmed_sent' ? styles.chipOk : styles.chip)

// Shown for the attempts that are still open; the other states need no explanation beyond their label.
export function explain(a: Pick<PushAttempt, 'status' | 'interrupted' | 'destination_label'>): string {
  if (a.status === 'in_progress') return `This push is being sent to ${a.destination_label} now. A merge touching its worker/days, and a second push over them, wait until it finishes.`
  if (a.status !== 'unknown') return ''
  return (
    `CONTROL does not know whether ${a.destination_label} received this push${a.interrupted ? ': the process sending it stopped before an answer was recorded' : ''}. ` +
    `Merging the people in it, and pushing the same worker/days again, are blocked until it is reconciled. ` +
    `Check ${a.destination_label}, then record what you found.`
  )
}

interface Props {
  // Reload when this changes (a push task has finished).
  tick: number
  // Holds the payroll_export power, the same as pushing. Others see the record, not the actions.
  mayReconcile: boolean
  // The attempt a link (from a refused merge) points at.
  focusId?: string | null
  // A reconciliation changes what a merge is allowed to do and what counts as exported.
  onChanged?: () => void
}

const SHOWN = 6

export function PushAttempts({ tick, mayReconcile, focusId, onChanged }: Props) {
  const { user } = useAuth()
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const [attempts, setAttempts] = useState<PushAttempt[] | null>(null)
  const [all, setAll] = useState(false)
  const [open, setOpen] = useState<{ id: string; outcome: 'confirmed_sent' | 'confirmed_not_sent' } | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const focusEl = useRef<HTMLLIElement | null>(null)

  const load = useCallback(async () => {
    if (!userRef.current) return
    try {
      const r = await apiJson<{ attempts: PushAttempt[] }>(userRef.current, '/api/wages/push-attempts')
      setAttempts(Array.isArray(r?.attempts) ? r.attempts : [])
    } catch {
      // Quiet: the page is usable without it, and the next fetch says so.
    }
  }, [uid])
  useEffect(() => {
    load()
  }, [load, tick])
  useRefetch(load)
  const anyOpen = !!attempts?.some((a) => a.blocking)
  useEffect(() => {
    if (!anyOpen) return
    const t = setInterval(load, 10000)
    return () => clearInterval(t)
  }, [anyOpen, load])
  useEffect(() => {
    if (focusId && attempts?.some((a) => a.attempt_id === focusId)) focusEl.current?.scrollIntoView?.({ block: 'center' })
  }, [focusId, attempts])

  if (!attempts || !attempts.length) return null
  const unresolved = attempts.filter((a) => a.blocking)
  // Open ones first, then newest; a focused one is always shown.
  const ordered = [...attempts].sort((a, b) => Number(b.blocking) - Number(a.blocking))
  const shown = all ? ordered : ordered.filter((a, i) => i < SHOWN || a.attempt_id === focusId)

  const start = (id: string, outcome: 'confirmed_sent' | 'confirmed_not_sent') => {
    setOpen({ id, outcome })
    setConfirmed(false)
    setNote('')
    setError(null)
  }
  const record = async () => {
    if (!open || !confirmed || busy) return
    setBusy(true)
    setError(null)
    try {
      await apiJson(userRef.current, `/api/wages/push-attempts/${open.id}/reconcile`, {
        body: { outcome: open.outcome, confirm: true, note: note.trim() || undefined },
      })
      setOpen(null)
      await load()
      onChanged?.()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record that. Nothing was changed.')
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <section id="push-attempts" className={`${styles.panel} ${unresolved.length ? styles.panelWarn : ''}`} aria-label="Pushes to providers" data-testid="push-attempts">
      <div className={styles.head}>
        <span className={styles.title}>Pushes to providers</span>
        <span className={styles.sub} data-testid="push-attempts-summary">
          {unresolved.length
            ? `${plural(unresolved.length, 'push is', 'pushes are')} unresolved. Merges and repeat pushes over their worker/days are blocked until each is resolved.`
            : 'Every push has an answer.'}
        </span>
      </div>
      <ul className={styles.list}>
        {shown.map((a) => {
          const text = explain(a)
          const asking = open?.id === a.attempt_id
          return (
            <li
              key={a.attempt_id}
              ref={a.attempt_id === focusId ? focusEl : undefined}
              className={`${styles.row} ${a.blocking ? styles.rowOpen : ''} ${a.attempt_id === focusId ? styles.rowFocus : ''}`}
              data-testid="push-attempt"
              data-attempt-id={a.attempt_id}
              data-status={a.status}
            >
              <div className={styles.line}>
                <span className={`${styles.chip} ${chipOf(a.status)}`} data-testid="push-attempt-status">
                  {STATUS_LABEL[a.status]}
                </span>
                <strong>{a.destination_label}</strong>
                <span>{rangeLabel(a.period_from, a.period_to)}</span>
                <span className={styles.meta}>
                  {plural(a.worker_days, 'worker-day', 'worker-days')} · {money(a.total_wages)}
                  {a.groups.length ? ` · ${a.groups.map((g) => g.group_name).join(', ')}` : ''}
                </span>
              </div>
              <div className={styles.meta}>
                Started {fmtStamp(a.created_at)}
                {a.status === 'failed' && a.message ? ` · ${a.message}` : ''}
                {a.resolved_at && a.resolved_by ? ` · recorded as ${a.status === 'confirmed_sent' ? 'sent' : 'not sent'} by ${a.resolved_by} on ${fmtStamp(a.resolved_at)}` : ''}
                {a.resolution_note ? ` · “${a.resolution_note}”` : ''}
              </div>
              {text && (
                <div className={styles.explain} data-testid="push-attempt-explain">
                  {text}
                </div>
              )}
              {a.status === 'unknown' && mayReconcile && !asking && (
                <div className={styles.actions}>
                  <button type="button" className={wages.smallBtn} onClick={() => start(a.attempt_id, 'confirmed_sent')}>
                    Confirmed sent
                  </button>
                  <button type="button" className={wages.smallBtn} onClick={() => start(a.attempt_id, 'confirmed_not_sent')}>
                    Confirmed not sent
                  </button>
                </div>
              )}
              {a.status === 'unknown' && !mayReconcile && <div className={styles.meta}>Someone who can push payroll needs to check {a.destination_label} and record what they found.</div>}
              {asking && open && (
                <div className={styles.form} role="group" aria-label="Record what you found">
                  <div>
                    {open.outcome === 'confirmed_sent'
                      ? `Record this push as sent. It will count as exported: ${plural(a.worker_days, 'worker-day', 'worker-days')} stay with the people who were in it, and a merge leaves them there.`
                      : `Record this push as not sent. Nothing counts as exported, and a merge or a new push over these worker/days is allowed again.`}
                  </div>
                  <label className={styles.check}>
                    <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                    <span>
                      I have checked {a.destination_label} and it {open.outcome === 'confirmed_sent' ? 'has' : 'does not have'} this push.
                    </span>
                  </label>
                  <textarea className={styles.note} aria-label="Note (optional)" placeholder="Note (optional)" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
                  {error && (
                    <div className={styles.err} role="alert">
                      {error}
                    </div>
                  )}
                  <div className={styles.actions}>
                    <button type="button" className={wages.primaryBtn} disabled={!confirmed || busy} onClick={record}>
                      {busy ? 'Recording…' : open.outcome === 'confirmed_sent' ? 'Record as sent' : 'Record as not sent'}
                    </button>
                    <button type="button" className={wages.ghostBtn} disabled={busy} onClick={() => setOpen(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {!all && attempts.length > shown.length && (
        <div className={styles.more}>
          <button type="button" className={wages.linkBtn} onClick={() => setAll(true)}>
            Show all {attempts.length} pushes
          </button>
        </div>
      )}
    </section>
  )
}
